import React, { useState } from 'react';
import { ProjectTaskDto, CreateProjectTaskRequest, UserDropdownDto } from '../../types';
import { X, AlertCircle } from 'lucide-react';
import { DateInputDDMMYYYY } from '../../components/shared/DateInputDDMMYYYY';

export interface TaskModalProps {
  isOpen: boolean;
  task?: ProjectTaskDto;
  onClose: () => void;
  onSave: (data: CreateProjectTaskRequest) => Promise<void>;
  onDelete?: () => Promise<void>;
  isAdmin: boolean;
  isLoading?: boolean;
  users?: UserDropdownDto[];
}

export const TaskModal: React.FC<TaskModalProps> = ({
  isOpen,
  task,
  onClose,
  onSave,
  onDelete,
  isAdmin,
  isLoading = false,
  users = [],
}) => {
  const [formData, setFormData] = useState<CreateProjectTaskRequest>({
    taskName: '',
    ownerUserId: undefined,
    startDate: '',
    dueDate: '',
    status: 'Not Started',
    priority: 'Medium',
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dateValidationError, setDateValidationError] = useState<string>('');

  React.useEffect(() => {
    if (task) {
      setFormData({
        taskName: task.taskName || '',
        ownerUserId: task.ownerUserId,
        startDate: task.startDate || '',
        dueDate: task.dueDate || '',
        status: task.status || 'Not Started',
        priority: task.priority || 'Medium',
        notes: task.notes || '',
      });
    } else {
      setFormData({
        taskName: '',
        ownerUserId: undefined,
        startDate: '',
        dueDate: '',
        status: 'Not Started',
        priority: 'Medium',
        notes: '',
      });
    }
  }, [task, isOpen]);

  const handleChange = (field: keyof CreateProjectTaskRequest, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setDateValidationError(''); // Clear error when user modifies dates
  };

  // Helper function to validate that due date is after start date
  const validateDates = (): boolean => {
    if (!formData.startDate || !formData.dueDate) {
      return true; // Skip validation if either date is empty
    }

    // Parse dates in ISO format (yyyy-mm-dd)
    const parseDate = (dateStr: string): Date | null => {
      // Check if it's already in ISO format (yyyy-mm-dd)
      if (dateStr.includes('-')) {
        const parts = dateStr.split('-');
        if (parts.length !== 3) return null;
        
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const day = parseInt(parts[2], 10);
        
        if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
        
        return new Date(year, month - 1, day);
      }
      
      return null;
    };

    const startDateObj = parseDate(formData.startDate);
    const dueDateObj = parseDate(formData.dueDate);

    if (!startDateObj || !dueDateObj) {
      return true; // Skip validation if dates are malformed
    }

    if (dueDateObj < startDateObj) {
      setDateValidationError('Due date must be after or equal to start date');
      return false;
    }

    setDateValidationError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.taskName.trim()) {
      alert('Please enter a task name');
      return;
    }

    // Validate dates before submitting
    if (!validateDates()) {
      return;
    }

    try {
      setIsSubmitting(true);
      await onSave(formData);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = async () => {
    if (onDelete) {
      try {
        setIsSubmitting(true);
        await onDelete();
        onClose();
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (!isOpen) return null;

  const isEditing = !!task;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9997,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '420px',
          maxHeight: '90vh',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
            {isEditing ? 'Edit Task' : 'New Task'}
          </h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              background: 'none',
              border: 'none',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              color: '#6b7280',
              opacity: isSubmitting ? 0.5 : 1,
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '16px', overflowY: 'auto', flexGrow: 1 }}>
          <div style={{ marginBottom: '12px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '4px',
              }}
            >
              Task Name *
            </label>
            <input
              type="text"
              value={formData.taskName}
              onChange={(e) => handleChange('taskName', e.target.value)}
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '13px',
                boxSizing: 'border-box',
                opacity: isSubmitting ? 0.6 : 1,
              }}
              placeholder="Enter task name"
              required
            />
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '4px',
              }}
            >
              Owner
            </label>
            <select
              value={formData.ownerUserId ?? ''}
              onChange={(e) => handleChange('ownerUserId', e.target.value ? Number(e.target.value) : undefined)}
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '13px',
                boxSizing: 'border-box',
                opacity: isSubmitting ? 0.6 : 1,
              }}
            >
              <option value="">Assign to...</option>
              {users.map((user) => (
                <option key={user.userId} value={user.userId}>
                  {user.displayName}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '4px',
                }}
              >
                Start Date
              </label>
              <DateInputDDMMYYYY
                value={formData.startDate || ''}
                onChange={(val) => handleChange('startDate', val)}
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '13px',
                  boxSizing: 'border-box',
                  opacity: isSubmitting ? 0.6 : 1,
                }}
              />
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '4px',
                }}
              >
                Due Date
              </label>
              <DateInputDDMMYYYY
                value={formData.dueDate || ''}
                onChange={(val) => handleChange('dueDate', val)}
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '13px',
                  boxSizing: 'border-box',
                  opacity: isSubmitting ? 0.6 : 1,
                }}
              />
            </div>
          </div>

          {dateValidationError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', backgroundColor: '#fee2e2', borderRadius: '4px', color: '#dc2626', fontSize: '12px', marginBottom: '12px' }}>
              <AlertCircle style={{ width: '16px', height: '16px', flexShrink: 0 }} />
              <span>{dateValidationError}</span>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '4px',
                }}
              >
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '13px',
                  boxSizing: 'border-box',
                  opacity: isSubmitting ? 0.6 : 1,
                }}
              >
                <option value="Not Started">Not Started</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="On Hold">On Hold</option>
              </select>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '4px',
                }}
              >
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => handleChange('priority', e.target.value)}
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '13px',
                  boxSizing: 'border-box',
                  opacity: isSubmitting ? 0.6 : 1,
                }}
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '4px',
              }}
            >
              Notes
            </label>
            <textarea
              value={formData.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value)}
              disabled={isSubmitting}
              rows={3}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '13px',
                boxSizing: 'border-box',
                opacity: isSubmitting ? 0.6 : 1,
                resize: 'vertical',
              }}
              placeholder="Add any notes or updates for this task..."
            />
          </div>

          {/* Action Buttons */}
          <div
            style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'space-between',
              paddingTop: '12px',
              borderTop: '1px solid #e5e7eb',
            }}
          >
            {/* Delete button (only for edit mode and admin) */}
            {isEditing && isAdmin && onDelete && (
              <button
                type="button"
                onClick={handleDeleteClick}
                disabled={isSubmitting}
                style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: 'none',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  opacity: isSubmitting ? 0.6 : 1,
                }}
              >
                Delete
              </button>
            )}

            {/* Right side buttons */}
            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid #d1d5db',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#374151',
                  opacity: isSubmitting ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || isLoading || !!dateValidationError}
                style={{
                  backgroundColor: isSubmitting || isLoading || dateValidationError ? '#d1d5db' : '#2563eb',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: 'none',
                  cursor: isSubmitting || isLoading || dateValidationError ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  opacity: isSubmitting || isLoading || dateValidationError ? 0.6 : 1,
                }}
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
